import React, { Component } from "react";
import * as Sentry from "@sentry/react";
import { T } from "../themes";

// ─── Per-Screen Error Boundary ──────────────────────────────────────────────
// Wraps individual screens so a crash in one tab doesn't take down the whole app.
// Usage: <ErrorBoundary screenName="Today"><TodayScreen/></ErrorBoundary>

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    Sentry.captureException(error, { extra: { screenName: this.props.screenName, componentStack: errorInfo?.componentStack } });
    if (process.env.NODE_ENV !== "production") {
      console.error(`[ErrorBoundary:${this.props.screenName || "unknown"}]`, error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState(prev => ({
      hasError: false,
      error: null,
      errorInfo: null,
      resetKey: prev.resetKey + 1,
    }));
  };

  render() {
    if (!this.state.hasError) {
      return (
        <React.Fragment key={this.state.resetKey}>
          {this.props.children}
        </React.Fragment>
      );
    }

    const screen = this.props.screenName || "this screen";
    return (
      <div style={{
        padding: "32px 20px",
        textAlign: "center",
        fontFamily: T.fontSans,
        maxWidth: 400,
        margin: "0 auto",
      }}>
        <div style={{
          fontSize: 40,
          marginBottom: 12,
        }}>⚠️</div>

        <h2 style={{
          color: T.accent,
          fontSize: 16,
          fontWeight: 900,
          margin: "0 0 8px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}>
          Something went wrong
        </h2>

        <p style={{
          color: T.textSub,
          fontSize: 12,
          fontWeight: "bold",
          margin: "0 0 20px",
          lineHeight: 1.5,
        }}>
          An error occurred on the <strong>{screen}</strong> screen.
          This is usually temporary.
        </p>

        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={this.handleReset}
            style={{
              background: T.accent,
              color: T.ink,
              border: T.border,
              padding: "10px 20px",
              fontSize: 11,
              fontFamily: T.fontSans,
              fontWeight: 900,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              borderRadius: T.radiusSm,
            }}
          >
            Try Again
          </button>

          <button
            onClick={() => window.location.reload()}
            style={{
              background: "transparent",
              color: T.textDim,
              border: T.border,
              padding: "10px 20px",
              fontSize: 11,
              fontFamily: T.fontSans,
              fontWeight: 900,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              borderRadius: T.radiusSm,
            }}
          >
            Reload App
          </button>
        </div>

        {process.env.NODE_ENV !== "production" && this.state.error && (
          <details style={{
            marginTop: 20,
            textAlign: "left",
            background: T.surface,
            border: T.border,
            borderRadius: T.radiusSm,
            padding: "10px 12px",
          }}>
            <summary style={{
              color: T.textDim,
              fontSize: 10,
              fontFamily: T.fontSans,
              fontWeight: "bold",
              cursor: "pointer",
            }}>
              Error details (dev only)
            </summary>
            <pre style={{
              color: T.red,
              fontSize: 9,
              fontFamily: T.fontMono,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              margin: "8px 0 0",
              maxHeight: 200,
              overflow: "auto",
            }}>
              {this.state.error.toString()}
              {"\n\n"}
              {this.state.errorInfo?.componentStack || ""}
            </pre>
          </details>
        )}
      </div>
    );
  }
}
